import { useState, useRef, useCallback, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronUp } from 'lucide-react'
import clsx from 'clsx'
import type { MenuProps } from '../../core/navigation'
import MenuUserInfo from './MenuUserInfo'

const MAGNIFICATION = 1.8
const DISTANCE_LIMIT = 150
const ICON_SIZE = 44
const AUTO_HIDE_DELAY = 3000
const TRIGGER_ZONE = 80

export default function FloatingDockMenu({ navigation, user, onLogout }: MenuProps) {
  const [dockVisible, setDockVisible] = useState(true)
  const [scales, setScales] = useState<number[]>([])
  const [openChildren, setOpenChildren] = useState<string | null>(null)
  const [showUserPopup, setShowUserPopup] = useState(false)
  const dockRef = useRef<HTMLDivElement>(null)
  const iconRefs = useRef<(HTMLDivElement | null)[]>([])
  const hideTimerRef = useRef<number>()
  const location = useLocation()

  // Top-level items only
  const topItems = navigation

  // Init scales
  useEffect(() => {
    setScales(new Array(topItems.length + 1).fill(1)) // +1 for user avatar
  }, [topItems.length])

  // Auto-hide logic
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (e.clientY > window.innerHeight - TRIGGER_ZONE) {
        setDockVisible(true)
        clearTimeout(hideTimerRef.current)
      } else {
        clearTimeout(hideTimerRef.current)
        hideTimerRef.current = window.setTimeout(() => {
          setDockVisible(false)
          setOpenChildren(null)
          setShowUserPopup(false)
        }, AUTO_HIDE_DELAY)
      }
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      clearTimeout(hideTimerRef.current)
    }
  }, [])

  // Close children when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dockRef.current && !dockRef.current.contains(e.target as Node)) {
        setOpenChildren(null)
        setShowUserPopup(false)
      }
    }
    window.addEventListener('click', handleClick)
    return () => window.removeEventListener('click', handleClick)
  }, [])

  // Close children on route change
  useEffect(() => {
    setOpenChildren(null)
    setShowUserPopup(false)
  }, [location.pathname])

  const handleDockMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dockRef.current) return
    const mouseX = e.clientX

    const newScales = iconRefs.current.map((ref) => {
      if (!ref) return 1
      const rect = ref.getBoundingClientRect()
      const iconCenterX = rect.left + rect.width / 2
      const distance = Math.abs(mouseX - iconCenterX)
      if (distance > DISTANCE_LIMIT) return 1
      return 1 + (MAGNIFICATION - 1) * Math.cos((distance / DISTANCE_LIMIT) * (Math.PI / 2))
    })

    setScales(newScales)
  }, [])

  const handleDockMouseLeave = useCallback(() => {
    setScales(new Array(topItems.length + 1).fill(1))
  }, [topItems.length])

  const handleItemClick = (item: typeof topItems[0], _index: number) => {
    if (item.children) {
      setOpenChildren(prev => prev === item.name ? null : item.name)
      setShowUserPopup(false)
    }
  }

  const isActive = (href: string) => {
    if (href === '/app') return location.pathname === '/app'
    return location.pathname.startsWith(href)
  }

  const isGroupActive = (item: typeof topItems[0]) => {
    if (item.href) return isActive(item.href)
    if (item.children) return item.children.some(c => isActive(c.href))
    return false
  }

  return (
    <div
      ref={dockRef}
      className="fixed bottom-4 left-1/2 z-30"
      style={{
        transform: `translateX(-50%) translateY(${dockVisible ? '0' : '100px'})`,
        transition: 'transform 0.3s ease',
        opacity: dockVisible ? 1 : 0,
      }}
      onMouseMove={handleDockMouseMove}
      onMouseLeave={handleDockMouseLeave}
    >
      {/* Children popup */}
      {openChildren && (() => {
        const parentItem = topItems.find(i => i.name === openChildren)
        const parentIndex = topItems.findIndex(i => i.name === openChildren)
        if (!parentItem?.children) return null

        return (
          <div
            className="absolute bottom-full mb-2 rounded-xl shadow-xl overflow-hidden py-1.5"
            style={{
              backgroundColor: 'var(--surface-card)',
              border: '1px solid var(--border-default)',
              left: `${(parentIndex * (ICON_SIZE + 8)) + ICON_SIZE / 2 + 12}px`,
              transform: 'translateX(-50%)',
              minWidth: '180px',
            }}
          >
            {parentItem.children.map((child) => (
              <NavLink
                key={child.href}
                to={child.href}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2.5 px-4 py-2 text-sm transition-colors',
                    isActive ? 'font-semibold' : ''
                  )
                }
                style={({ isActive }) => ({
                  color: isActive ? 'var(--primary-600)' : 'var(--text-primary)',
                  backgroundColor: isActive ? 'var(--hover-bg)' : 'transparent',
                })}
              >
                <child.icon className="w-4 h-4" />
                {child.name}
              </NavLink>
            ))}
          </div>
        )
      })()}

      {/* User popup */}
      {showUserPopup && (
        <div
          className="absolute bottom-full mb-2 right-0 rounded-xl shadow-xl overflow-hidden p-3"
          style={{
            backgroundColor: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            minWidth: '200px',
          }}
        >
          <MenuUserInfo user={user} onLogout={onLogout} />
        </div>
      )}

      {/* Dock bar */}
      <div
        className="dock-bar flex items-end gap-2 px-3 py-2 rounded-2xl"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--surface-card) 80%, transparent)',
          border: '1px solid var(--border-default)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        {topItems.map((item, index) => {
          const scale = scales[index] || 1
          const active = isGroupActive(item)
          const hasChildren = !!item.children

          const iconContent = (
            <div
              ref={el => { iconRefs.current[index] = el }}
              className="dock-icon relative flex flex-col items-center cursor-pointer group"
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'bottom center',
                transition: 'transform 0.15s ease-out',
              }}
              onClick={(e) => {
                if (hasChildren) {
                  e.preventDefault()
                  e.stopPropagation()
                  handleItemClick(item, index)
                }
              }}
            >
              {/* Tooltip */}
              <div
                className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
                style={{
                  backgroundColor: 'var(--surface-elevated)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)',
                }}
              >
                {item.name}
              </div>
              {/* Icon */}
              <div
                className="rounded-xl flex items-center justify-center transition-colors"
                style={{
                  width: `${ICON_SIZE}px`,
                  height: `${ICON_SIZE}px`,
                  backgroundColor: active ? 'var(--primary-600)' : 'transparent',
                  color: active ? 'white' : 'var(--text-primary)',
                }}
              >
                <item.icon className="w-5 h-5" />
                {/* Badge */}
                {item.badge && item.badge > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {item.badge}
                  </span>
                )}
                {/* Children indicator */}
                {hasChildren && (
                  <ChevronUp className="absolute -bottom-0.5 w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                )}
              </div>
              {/* Active dot */}
              {active && (
                <div className="w-1 h-1 rounded-full mt-0.5" style={{ backgroundColor: 'var(--primary-500)' }} />
              )}
            </div>
          )

          if (item.href && !hasChildren) {
            return (
              <NavLink key={item.name} to={item.href} end={item.href === '/app'}>
                {iconContent}
              </NavLink>
            )
          }

          return <div key={item.name}>{iconContent}</div>
        })}

        {/* Separator */}
        <div className="w-px h-8 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />

        {/* User avatar */}
        <div
          ref={el => { iconRefs.current[topItems.length] = el }}
          className="dock-icon relative flex flex-col items-center cursor-pointer group"
          style={{
            transform: `scale(${scales[topItems.length] || 1})`,
            transformOrigin: 'bottom center',
            transition: 'transform 0.15s ease-out',
          }}
          onClick={(e) => {
            e.stopPropagation()
            setShowUserPopup(prev => !prev)
            setOpenChildren(null)
          }}
        >
          <div
            className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
            style={{
              backgroundColor: 'var(--surface-elevated)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-default)',
            }}
          >
            {user?.username || 'Account'}
          </div>
          <div
            className="rounded-xl flex items-center justify-center"
            style={{
              width: `${ICON_SIZE}px`,
              height: `${ICON_SIZE}px`,
              backgroundColor: 'var(--primary-600)',
            }}
          >
            <span className="font-medium text-white text-sm">
              {user?.email?.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
